import {
  Component,
  ChangeDetectionStrategy,
  inject,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { SymbolHoverService } from '@core/services/symbol-hover.service';

/**
 * Shared symbol-hover overlay (GAP-FU-10).
 *
 * Single instance mounted at app root. Visibility / position driven by
 * `SymbolHoverService`. When the service emits a new anchor rect the
 * card repositions just below it (clamped to the viewport).
 */
@Component({
  selector: 'app-symbol-hover-card',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './symbol-hover-card.component.html',
  styleUrls: ['./symbol-hover-card.component.scss'],
})
export class SymbolHoverCardComponent {
  private readonly svc = inject(SymbolHoverService);

  protected readonly symbol = this.svc.symbol;
  protected readonly quote = this.svc.quote;
  protected readonly error = this.svc.error;

  protected readonly position = computed(() => {
    const anchor = this.svc.anchor();
    if (!anchor) return null;
    // Position the card directly below the anchor, clamped to viewport.
    const viewportW = typeof window !== 'undefined' ? window.innerWidth : 1280;
    const cardWidth = 260;
    const left = Math.min(anchor.left, viewportW - cardWidth - 8);
    const top = anchor.bottom + 6;
    return { left, top };
  });

  protected readonly deltaSign = computed(() => {
    const d = this.quote()?.delta1d ?? 0;
    if (d > 0) return 'up';
    if (d < 0) return 'down';
    return 'flat';
  });

  protected sparklinePath(prices: readonly number[]): string {
    if (prices.length < 2) return '';
    const width = 220;
    const height = 32;
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const range = max - min || 1;
    const step = width / (prices.length - 1);
    let d = '';
    prices.forEach((p, i) => {
      const x = i * step;
      const y = height - ((p - min) / range) * height;
      d += `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)} `;
    });
    return d.trim();
  }
}
